import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Eye, EyeOff, Sparkles } from 'lucide-react';

const PARTNER_LABELS = {
  dj: 'DJ / Entertainer',
  caterer: 'Caterer',
  staff: 'Event Staffing provider',
  photographer: 'Photographer / Videographer',
  florist: 'Florist / Decor pro',
  rental: 'Rental supplier',
  chef: 'Private chef',
  band: 'Musician / Band',
};

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  const partner = searchParams.get('partner');
  const presetRole = searchParams.get('role');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(presetRole || 'client');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const user = await register(email, password, name, role);
      toast.success(`Welcome to Eventism, ${user.name}!`);
      if (returnTo) { navigate(returnTo); return; }
      switch (user.role) {
        case 'admin': navigate('/admin'); break;
        case 'vendor': navigate('/vendor'); break;
        case 'venue_owner': navigate('/venue-onboarding'); break;
        case 'sales_agent': navigate('/sales/apply'); break;
        default: navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md relative z-10 bg-card/90 backdrop-blur-xl border-border">
        <CardHeader className="text-center">
          <Link to="/" className="inline-flex items-center justify-center space-x-2 mb-4">
            <img src="/eventism-logo.png" alt="Eventism" className="h-7 sm:h-8" style={{ filter: 'brightness(0) invert(1)' }} />
          </Link>
          <CardTitle className="text-2xl font-heading text-foreground">
            {partner ? `Join as a ${PARTNER_LABELS[partner] || 'partner'}` : 'Create an account'}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {partner
              ? "Takes 30 seconds — then add your first service."
              : 'Join thousands of event professionals'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Google OAuth */}
          <Button 
            type="button"
            variant="outline" 
            className="w-full border-border hover:bg-muted text-foreground"
            onClick={loginWithGoogle}
            data-testid="google-register-btn"
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full bg-muted" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-muted-foreground">Full Name</Label>
              <Input
                id="name"
                type="text"
                placeholder="John Doe"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                data-testid="register-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-muted-foreground">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="name@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
                data-testid="register-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-muted-foreground">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-muted border-border text-foreground placeholder:text-muted-foreground pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="role" className="text-muted-foreground">I am a...</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="bg-muted border-border text-foreground" data-testid="register-role">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  <SelectItem value="client" className="text-foreground hover:bg-accent">Event Host / Client</SelectItem>
                  <SelectItem value="vendor" className="text-foreground hover:bg-accent">Vendor / Gig Worker</SelectItem>
                  <SelectItem value="venue_owner" className="text-foreground hover:bg-accent">Venue Owner / Partner</SelectItem>
                  <SelectItem value="sales_agent" className="text-foreground hover:bg-accent">Sales Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-indigo-500 hover:bg-indigo-600 text-foreground"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <p className="text-center text-sm text-muted-foreground">
            Already have an account?{' '}
            <Link to={returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login'} className="text-indigo-400 hover:text-indigo-300 font-medium">
              Sign in
            </Link>
          </p>

          <p className="text-center text-xs text-muted-foreground">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="underline hover:text-muted-foreground">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy" className="underline hover:text-muted-foreground">Privacy Policy</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
